import type { SharedVolume, SharedVolumeExistingPath, SharedVolumeState } from "@/lib/api";
import { splitPath } from "@/lib/environment-files";
import { formatBytes } from "@/lib/format-bytes";
import { formatRelativeDateTime } from "@/lib/format-datetime";

/** 作成中・削除中のボリュームがある間、一覧を再取得する間隔 (ms)。 */
export const SHARED_VOLUME_POLL_INTERVAL_MS = 3000;

export const SHARED_VOLUME_STATE_LABELS: Readonly<Record<SharedVolumeState, string>> = {
	creating: "作成中",
	ready: "利用可能",
	deleting: "削除中",
	error: "エラー",
};

/** 実体の用意・破棄がバックグラウンドで進行中か (一覧のポーリング対象)。 */
export function isSharedVolumeTransitional(state: SharedVolumeState): boolean {
	return state === "creating" || state === "deleting";
}

/** 環境に新たにマウントできるか (利用可能なもののみ)。 */
export function isSharedVolumeMountable(volume: Pick<SharedVolume, "state">): boolean {
	return volume.state === "ready";
}

/**
 * 削除ボタンを無効にする理由。null なら削除できる。サーバー側の 409 と同じ条件を UI で先に示す
 * (マウント中の環境がある・遷移中)。
 */
export function sharedVolumeDeleteDisabledReason(volume: SharedVolume): string | null {
	if (isSharedVolumeTransitional(volume.state)) {
		return `${SHARED_VOLUME_STATE_LABELS[volume.state]}のため削除できません。`;
	}
	if (volume.attachedEnvironments.length > 0) {
		return "マウント中のエージェントがあります。先に各エージェントの設定からマウントを解除してください。";
	}
	return null;
}

/** 使用容量の表示。概算であることを明示する。未計測なら「未計測」。 */
export function formatSharedVolumeUsage(volume: Pick<SharedVolume, "measuredBytes">): string {
	if (volume.measuredBytes === null) return "未計測";
	return `約 ${formatBytes(volume.measuredBytes)}`;
}

/** 使用容量の最終計測時刻の表示。未計測なら「未計測」。 */
export function formatSharedVolumeMeasuredAt(volume: Pick<SharedVolume, "measuredAt">): string {
	return volume.measuredAt === null ? "未計測" : formatRelativeDateTime(volume.measuredAt);
}

/** 置くもの (置き場所からの相対パス) の一番上の名前ごとに、根からのパスとフォルダか (同じ名前の確認はこれで足りる)。 */
export function sharedVolumeTopLevelEntries(
	directory: string,
	relativePaths: readonly string[],
): SharedVolumeExistingPath[] {
	const names = new Map<string, boolean>();
	for (const path of relativePaths) {
		const slash = path.indexOf("/");
		const name = slash === -1 ? path : path.slice(0, slash);
		names.set(name, (names.get(name) ?? false) || slash !== -1);
	}
	return [...names].map(([name, isDirectory]) => ({
		path: directory === "" ? name : `${directory}/${name}`,
		directory: isDirectory,
	}));
}

/** 共有ボリューム (デバイスは写し) に在るものとフォルダ・ファイルが食い違うものがあれば、置けない理由。無ければ null。 */
export function sharedVolumeDropConflict(
	dropped: readonly SharedVolumeExistingPath[],
	existing: readonly SharedVolumeExistingPath[],
): string | null {
	const directories = new Map(dropped.map((entry) => [entry.path, entry.directory]));
	const conflict = existing.find((entry) => directories.get(entry.path) !== entry.directory);
	if (conflict === undefined) return null;
	return `同じ名前の${conflict.directory ? "フォルダ" : "ファイル"}があるため置けません: ${splitPath(conflict.path).name}`;
}
